import React, { useEffect, useState } from 'react';
import { ChevronLeftIcon, CalendarIcon } from '@heroicons/react/16/solid';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Heading, Subheading } from '../../../../components/Heading';
import { DescriptionDetails, DescriptionList, DescriptionTerm } from '../../../../components/Description-List';
import { Button } from '../../../../components/Button';
import { Card } from '../../../../components/Card';
import { AndroidRelease, AppReleaseService } from '../../../../services/appReleaseService';
import showErrorToast from '../../../../components/Toasts/ShowErrorToast';
import dateFormat from 'dateformat';
import Loading from '../../../../components/Loading';

const ViewAppUpdateIdPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [release, setRelease] = useState<AndroidRelease | null>(null);

    useEffect(() => {
        document.title = 'View App Update';
    }, []);

    useEffect(() => {
        if (!id) return;
        const load = async () => {
            try {
                setLoading(true);
                const data = await AppReleaseService.getAndroidReleaseById(Number(id));
                setRelease(data);
            } catch (error: any) {
                showErrorToast(error?.response?.data?.message || 'Failed to fetch app update');
            } finally {
                setLoading(false);
            }
        };
        void load();
    }, [id]);

    const formatFileSize = (bytes: number) => {
        if (!bytes || bytes <= 0) return '-';
        const mb = bytes / (1024 * 1024);
        return `${mb.toFixed(2)} MB`;
    };

    if (loading) return <Loading />;
    if (!release) return <p className="py-16 text-center">App release not found.</p>;

    return (
        <>
            <div className="max-lg:hidden">
                <Link to="/gp/app/updates" className="inline-flex items-center gap-2 text-sm/6 text-zinc-500 dark:text-zinc-400">
                    <ChevronLeftIcon className="size-4 fill-zinc-400 dark:fill-zinc-500" />
                    App Updates
                </Link>
            </div>
            <div className="mt-4 lg:mt-8">
                <div className="flex items-center gap-4">
                    <Heading>{release.version_name}</Heading>
                </div>
                <div className="isolate mt-2.5 flex flex-wrap justify-between gap-x-6 gap-y-4">
                    <div className="flex flex-wrap gap-x-10 gap-y-4 py-1.5">
                        <span className="flex items-center gap-2 text-base/6 text-zinc-950 sm:text-sm/6 dark:text-white">
                            <CalendarIcon className="size-4 shrink-0 fill-zinc-400 dark:fill-zinc-500" />
                            <span>{dateFormat(release.created_time, 'mmmm dd, yyyy')}</span>
                        </span>
                    </div>
                    <div className="flex gap-4">
                        <Button onClick={() => navigate(`/gp/app/updates/edit/${release.id}`)}>Edit</Button>
                    </div>
                </div>
            </div>

            <Card className="mt-12 p-6">
                <Subheading>Release Details</Subheading>
                <DescriptionList className="mt-4">
                    <DescriptionTerm>Version Name</DescriptionTerm>
                    <DescriptionDetails>{release.version_name}</DescriptionDetails>
                    <DescriptionTerm>Version Code</DescriptionTerm>
                    <DescriptionDetails>{release.version_code}</DescriptionDetails>
                    <DescriptionTerm>File Name</DescriptionTerm>
                    <DescriptionDetails>{release.file_name}</DescriptionDetails>
                    <DescriptionTerm>File Size</DescriptionTerm>
                    <DescriptionDetails>{formatFileSize(release.file_size)}</DescriptionDetails>
                    <DescriptionTerm>Status</DescriptionTerm>
                    <DescriptionDetails>{release.is_active === 1 ? 'Active' : 'Inactive'}</DescriptionDetails>
                    <DescriptionTerm>Release Notes</DescriptionTerm>
                    <DescriptionDetails>{release.release_notes || '-'}</DescriptionDetails>
                    <DescriptionTerm>APK URL</DescriptionTerm>
                    <DescriptionDetails>
                        <a href={release.apk_url} target="_blank" rel="noreferrer" className="text-emerald-700 underline">
                            Open APK
                        </a>
                    </DescriptionDetails>
                </DescriptionList>
            </Card>
        </>
    );
};

export default ViewAppUpdateIdPage;
